import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

export const CLIENT_FORBIDDEN_WORKSPACE_TABS = new Set([
  'work',
  'team',
  'budget',
  'payments',
  'messages',
  'onboarding',
  'rewards',
]);

export const TESTER_FORBIDDEN_WORKSPACE_TABS = new Set([
  'work',
  'team',
  'budget',
  'payments',
  'activity',
  'onboarding',
  'results',
]);

export function normalizeClientWorkspaceTab(tab) {
  const segment = (tab || '').split('/')[0];
  if (!segment || CLIENT_FORBIDDEN_WORKSPACE_TABS.has(segment)) {
    return 'setup';
  }
  return segment;
}

/**
 * Redirects client / client_team away from staff-only workspace segments.
 */
export default function ClientWorkspaceTabGuard({ children }) {
  const { user } = useSelector((state) => state.auth);
  const { id: projectId, cycleId } = useParams();
  const location = useLocation();
  const role = String(user?.role || '').toLowerCase();
  const isClient = role === 'client' || role === 'client_team';
  const isTester = role === 'tester';
  const segment = location.pathname.split('/workspace/')[1]?.split('/')[0];

  if (isTester && segment && TESTER_FORBIDDEN_WORKSPACE_TABS.has(segment)) {
    const base = projectId
      ? `/app/projects/${projectId}/cycles/${cycleId}/workspace`
      : `/app/test-cycles/${cycleId}/workspace`;
    return <Navigate to={`${base}/test-cases`} replace />;
  }

  if (!isClient) {
    return children;
  }

  if (segment && !CLIENT_FORBIDDEN_WORKSPACE_TABS.has(segment)) {
    return children;
  }

  const base = projectId
    ? `/app/projects/${projectId}/cycles/${cycleId}/workspace`
    : `/app/test-cycles/${cycleId}/workspace`;

  return <Navigate to={`${base}/setup`} replace />;
}

/**
 * Rewards and other tester-only workspace segments.
 */
export function TesterOnlyWorkspaceTabGuard({ children }) {
  const { user } = useSelector((state) => state.auth);
  const { id: projectId, cycleId } = useParams();

  if (String(user?.role || '').toLowerCase() === 'tester') {
    return children;
  }

  const base = projectId
    ? `/app/projects/${projectId}/cycles/${cycleId}/workspace`
    : `/app/test-cycles/${cycleId}/workspace`;

  return <Navigate to={`${base}/setup`} replace />;
}

export function PlatformOwnerWorkspaceTabGuard({ children }) {
  const { user } = useSelector((state) => state.auth);
  const { id: projectId, cycleId } = useParams();

  if (['admin', 'platform_owner'].includes(String(user?.role || '').toLowerCase())) {
    return children;
  }

  const base = projectId
    ? `/app/projects/${projectId}/cycles/${cycleId}/workspace`
    : `/app/test-cycles/${cycleId}/workspace`;

  return <Navigate to={`${base}/setup`} replace />;
}
